import React, { useState } from "react";
import { Dialog } from "@/components/ui/dialog/dialog";
import { StandardFrame } from "@/components/ui/frames/standard-frame";
import { SimpleButton } from "@/components/ui/buttons/simple-button";
import { RemoveIcon, CheckIcon } from "@/icons";
import { createBox } from "@/services/box/create-box";
import { useAuth } from "@/providers/auth-provider";
import { Alert } from "@/components/alert/alert";
import cn from "classnames";

interface CreateBoxDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const PRESET_COLORS = [
  "#3b2c35", // Dark Gray
  "#6c447d", // Purple
  "#a8a878", // Normal
  "#f08030", // Fire
  "#6890f0", // Water
  "#f8d030", // Electric
  "#78c850", // Grass
  "#98d8d8", // Ice
  "#c03028", // Fighting
  "#a040a0", // Poison
  "#e0c068", // Ground
  "#a890f0", // Flying
  "#f85888", // Psychic
  "#a8b820", // Bug
  "#b8a038", // Rock
  "#705898", // Ghost
  "#7038f8", // Dragon
  "#705848", // Dark
  "#b8b8d0", // Steel
  "#ee99ac", // Fairy
];

const PRESET_ICONS = [
  "box",
  "bug",
  "card",
  "dark",
  "dice",
  "dragon",
  "electric",
  "eye",
  "fairy",
  "fighting",
  "fire",
  "flying",
  "ghost",
  "grass",
  "ground",
  "hanger",
  "hat",
  "heart",
  "ice",
  "normal",
  "poison",
  "psychic",
  "puzzle",
  "rock",
  "shopping-basket",
  "steel",
  "swap",
  "water",
  "unknown",
  "lock",
];

export const CreateBoxDialog = ({
  isOpen,
  onClose,
  onSuccess,
}: CreateBoxDialogProps) => {
  const { getAccessToken } = useAuth();
  const [name, setName] = useState("");
  const [selectedColor, setSelectedColor] = useState(PRESET_COLORS[0]);
  const [selectedIcon, setSelectedIcon] = useState(PRESET_ICONS[0]);
  const [isCreating, setIsCreating] = useState(false);

  const handleCreate = async () => {
    if (!name.trim()) {
      Alert("Please enter a squad name");
      return;
    }

    if (!getAccessToken()) return;

    setIsCreating(true);
    try {
      const response = await createBox({
        name: name.trim(),
        ...(selectedColor && { color: selectedColor }),
        ...(selectedIcon && { icon: selectedIcon }),
      });

      if (response.ok) {
        Alert("Squad created successfully!");
        setName("");
        onSuccess();
        onClose();
      } else {
        Alert(response.message || "Failed to create squad");
      }
    } catch (error) {
      console.error(error);
      Alert("An error occurred while creating the squad");
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <Dialog isOpen={isOpen} onClose={onClose} closeOnBackdrop={!isCreating}>
      <StandardFrame
        variant="purple"
        className="w-[90%] max-w-md"
      >
        <div className="flex flex-col gap-4 p-3">
        <div className="flex items-center justify-between border-b border-white/10 pb-2 px-2">
          <h3 className="text-lg font-bold text-white uppercase tracking-wider">
            Create New Squad
          </h3>
          <button onClick={onClose} className="text-white/50 hover:text-white">
            <RemoveIcon width={32} height={32} className="invert" />
          </button>
        </div>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-bold text-white/70 uppercase">
              Squad Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. My Favorites"
              className="w-full bg-black/40 border-2 border-white/10 rounded-lg px-3 py-2 text-white placeholder-white/30 focus:outline-none focus:border-pk-orange-4 transition-colors"
              autoFocus
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs font-bold text-white/70 uppercase">
              Color
            </label>
            <div className="flex flex-wrap justify-start gap-1 max-h-32 overflow-y-auto p-1 bg-black/10 rounded-lg">
              {PRESET_COLORS.map((color) => (
                <button
                  key={color}
                  onClick={() => setSelectedColor(color)}
                  className={cn(
                    "w-8 h-8 grow rounded-lg border-2 transition-all active:scale-90",
                    selectedColor === color
                      ? "border-white scale-110 shadow-lg"
                      : "border-transparent opacity-60 hover:opacity-100",
                  )}
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs font-bold text-white/70 uppercase">
              Icon
            </label>
            <div className="grid grid-cols-8 gap-1 bg-black/20 p-2 rounded-lg max-h-48 overflow-y-auto">
              {PRESET_ICONS.map((icon) => (
                <button
                  key={icon}
                  onClick={() => setSelectedIcon(icon)}
                  className={cn(
                    "p-1 rounded transition-all active:scale-90 flex items-center justify-center h-8 w-8",
                    selectedIcon === icon
                      ? "bg-white/20 border border-white/40"
                      : "hover:bg-white/5",
                  )}
                >
                  <img
                    src={`/assets/icons/boxes/${icon}.svg`}
                    className={cn(
                      "w-6 h-6 drop-shadow-sm",
                      selectedIcon === icon ? "opacity-100" : "opacity-40",
                    )}
                    alt={icon}
                  />
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex gap-2 mt-2">
          <SimpleButton onClick={onClose} variant="gray" className="flex-1">
            Cancel
          </SimpleButton>
          <SimpleButton
            onClick={handleCreate}
            variant="green"
            className="flex-1"
            disabled={isCreating || !name.trim()}
          >
            {isCreating ? "Creating..." : "Create Squad"}
          </SimpleButton>
        </div>
        </div>
      </StandardFrame>
    </Dialog>
  );
};
